import {
  FORMAT_CONVERSION_CREDIT_COST,
  FORMAT_PRESET_DIMENSIONS,
  type GeneratedAsset,
} from '@pulsify/shared/dto'
import { Wand2 } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useConvertFormat } from '~/features/ai-studio/hooks'
import { AiProcessingOverlay } from '~/features/shared/components/ai-processing-overlay'
import {
  CreditCostNote,
  CreditCostTag,
  useCreditCost,
} from '~/features/shared/components/credit-cost'
import type { CoverControls } from '~/features/shared/components/images-section'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { extensionFor, urlToFile } from '~/shared/lib/asset-image'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { FormatFocusField } from './format-focus-field'

const COVER = FORMAT_PRESET_DIMENSIONS['pulsify-cover']
const COVER_RATIO = COVER.width / COVER.height
const RATIO_TOLERANCE = 0.08

function useImageRatio(src: string | undefined): number | null {
  const [ratio, setRatio] = useState<number | null>(null)
  useEffect(() => {
    setRatio(null)
    if (!src) return
    const img = new Image()
    img.onload = () => setRatio(img.naturalWidth / img.naturalHeight)
    img.src = src
    return () => {
      img.onload = null
    }
  }, [src])
  return ratio
}

function coverNeedsFit(ratio: number | null): boolean {
  return ratio !== null && Math.abs(ratio / COVER_RATIO - 1) > RATIO_TOLERANCE
}

async function coverSource(cover: CoverControls): Promise<File> {
  if (cover.file) return cover.file
  const blob = await (await fetch(cover.preview ?? '')).blob()
  return new File([blob], 'cover', { type: blob.type || 'image/jpeg' })
}

/** Offers to let the image model rearrange a cover that doesn't fit the cover format. */
export function CoverFitButton({ cover }: { cover: CoverControls }) {
  const { data: business } = useCurrentBusiness()
  const ratio = useImageRatio(cover.preview)
  const [open, setOpen] = useState(false)

  if (!business || !coverNeedsFit(ratio)) return null

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        title={m.coverFitHint({ credits: FORMAT_CONVERSION_CREDIT_COST })}
      >
        <Wand2 />
        {m.coverFit()}
        <CreditCostTag cost={FORMAT_CONVERSION_CREDIT_COST} />
      </Button>
      {open && (
        <CoverFitDialog businessId={business.id} cover={cover} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

function CoverFitDialog({
  businessId,
  cover,
  onClose,
}: {
  businessId: string
  cover: CoverControls
  onClose: () => void
}) {
  const convert = useConvertFormat(businessId)
  const { state: cost } = useCreditCost(businessId, FORMAT_CONVERSION_CREDIT_COST)
  const [focus, setFocus] = useState('')
  const [result, setResult] = useState<GeneratedAsset | null>(null)
  const busy = convert.isPending

  async function adjust() {
    try {
      const res = await convert.mutateAsync({
        file: await coverSource(cover),
        presets: ['pulsify-cover'],
        output: 'jpeg',
        focus,
      })
      setResult(res.assets[0] ?? null)
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  async function use() {
    if (!result) return
    try {
      cover.put(await urlToFile(result.url, `cover.${extensionFor(result.mimeType)}`))
      toast.success(m.coverFitDone())
      onClose()
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <>
      <AiProcessingOverlay open={busy} />
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next && !busy) onClose()
        }}
      >
        <DialogContent showCloseButton={!busy} className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{m.coverFitTitle()}</DialogTitle>
            <DialogDescription>{m.coverFitDescription()}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <CoverFrame label={m.coverFitBefore()}>
              <img src={cover.preview} alt="" className="size-full object-contain" />
            </CoverFrame>
            <CoverFrame label={m.coverFitAfter()}>
              {result ? (
                <img src={result.url} alt="" className="size-full object-cover" />
              ) : (
                <span className="px-6 text-center text-muted-foreground text-sm">
                  {m.coverFitEmpty()}
                </span>
              )}
            </CoverFrame>
          </div>

          <FormatFocusField value={focus} onChange={setFocus} rows={3} />

          <div className="flex flex-wrap items-end justify-between gap-2">
            <div className="flex flex-col gap-1">
              <Button
                type="button"
                variant={result ? 'outline' : 'default'}
                onClick={adjust}
                disabled={busy || cost === 'short'}
              >
                {busy ? (
                  <PulsifyLoader className="size-5" tone={result ? undefined : 'on-brand'} />
                ) : (
                  <Wand2 />
                )}
                {busy ? m.coverFitWorking() : result ? m.coverFitAgain() : m.coverFitStart()}
                {!busy && <CreditCostTag cost={FORMAT_CONVERSION_CREDIT_COST} />}
              </Button>
              <CreditCostNote
                businessId={businessId}
                cost={FORMAT_CONVERSION_CREDIT_COST}
                className="text-xs"
              />
            </div>
            {result && (
              <Button type="button" onClick={use} disabled={busy}>
                {m.coverFitUse()}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function CoverFrame({ label, children }: { label: string; children: ReactNode }) {
  return (
    <figure className="space-y-1.5">
      <figcaption className="text-muted-foreground text-xs">{label}</figcaption>
      <div className="flex aspect-[3/2] items-center justify-center overflow-hidden rounded-lg border bg-muted">
        {children}
      </div>
    </figure>
  )
}
